// https://babeljs.io/blog/2019/03/19/7.4.0#migration-from-core-js-2
import 'core-js/stable';
import 'regenerator-runtime/runtime';

import 'css/app.scss';
import 'react-dates/lib/css/_datepicker.css';

// should be imported before other code to run first
import { config } from './setup';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as Cookies from 'js-cookie';
import { loadableReady } from '@loadable/component';
import { SheetsRegistry } from 'react-jss/lib/jss';
import { ReduxAsyncConnect } from 'ha/modules/ReduxConnect';
import {
  applyRouterMiddleware,
  browserHistory,
  match,
  Router,
} from 'react-router';
import { useScroll } from 'react-router-scroll';

import { createIntl } from 'ha/i18n';
import getAppServices from 'ha/services/getAppServices';

import AppContextProvider from 'ha/myredux/AppContextProvider';

import createAppStore from 'ha/myredux/createAppStore';
import getInitialState from 'ha/myredux/getInitialState';

import reportError from 'ha/helpers/bugsnag/reportError';
import isIgnoredHosts from 'ha/utils/isIgnoredHosts';

import experiments from 'ha/constants/experiments';
import CookiesConstants from 'ha/constants/Cookies';
import { EnvFlags } from 'ha/constants/FeatureFlags';
import initSession from 'ha/modules/AuthLogic/actions/initSession';
import { initExperiments } from 'ha/modules/Experiments';
import { setupMediaQueryListeners } from 'ha/modules/UI/actions';
import { getUserId } from 'ha/modules/AuthLogic/selectors';
import { isFeatureEnabled } from 'ha/modules/FeatureFlag/selectors';
import getLanguageFromUrl from 'ha/utils/getLanguageFromUrl';
import { track } from 'ha/modules/Analytics/actions';
import handleInstallPrompt from 'ha/serviceWorker/utils/handleInstallPrompt';
import initServiceWorker from 'ha/serviceWorker/utils/initServiceWorker';
import getHaSwUrl from 'ha/serviceWorker/utils/getHaSwUrl';
import loadNonCriticalData from 'ha/utils/dom/loadNonCriticalData';

import makeGraphqlClient from 'ha/services/graphqlClient';
import FetchClient from 'ha/helpers/FetchClient';

import getRoutes from './getRoutes';

if (global.Bugsnag) {
  global.Bugsnag.app.version = `${__VERSION__}__${__COMMITHASH__}`;
}

/* eslint-disable no-underscore-dangle */
const preloadedState = global.__PRELOADED_STATE__ || {};
/* eslint-enable no-underscore-dangle */

const rootEl = document.getElementById('app-root');

const lang = getLanguageFromUrl(window.location.pathname);
const intl = createIntl({
  lang,
  messages: global.Translations,
  baseDomain: config.baseDomain,
});

global.baseUrl = config.baseDomain;

const fetchOptions = {
  headers: {
    'X-HA-Lang': lang,
  },
};

const haApi = new FetchClient(config.backend.host, fetchOptions);

const services = getAppServices({
  config,
  clients: {
    haApi,
    moneta: new FetchClient(config.moneta.host),
    osiris: new FetchClient(config.osiris.host),
    hermes: new FetchClient(config.hermes.host, {
      credentials: 'omit',
    }),
    graphql: makeGraphqlClient(
      new FetchClient(config.graphql.host, fetchOptions),
    ),
  },
  lang,
});

const store = createAppStore({
  config,
  cookies: Cookies,
  errorTracker: global.Bugsnag,
  history: browserHistory,
  initialState: getInitialState({
    preloadedState,
    language: lang,
  }),
  services,
});

window.addEventListener(
  'beforeinstallprompt',
  handleInstallPrompt({
    onChoice: choiceResult =>
      store.dispatch(
        track('Web App Install Banner', { action: choiceResult.outcome }),
      ),
    onError: reportError,
  }),
);

Object.defineProperty(global, 'reduxStore', {
  get: () => store,
  set: () => {
    throw new Error('Trying to modify `window.reduxStore` property!');
  },
});

Promise.all([
  new Promise(resolve => loadableReady(resolve)),
  initSession({
    lang,
    store,
  }),
])
  .catch(reportError)
  .then(() => {
    const userId = getUserId(store.getState());

    if (global.Bugsnag && userId) {
      global.Bugsnag.user = {
        id: userId,
      };
    }

    if (userId) {
      global.document.body.className += ' body--has-bottom-nav';
    }
  })
  .then(() => {
    store.dispatch(
      initExperiments({
        experiments,
        userIdentifier: Cookies.get(
          CookiesConstants.USER_IDENTIFIER_COOKIE_NAME,
        ),
      }),
    );
  })
  .then(
    () =>
      new Promise(resolve => {
        if (isIgnoredHosts(global.location.host)) {
          resolve();
        } else {
          const sheetsRegistry = new SheetsRegistry();
          const routes = getRoutes(store, intl.urlResolver);
          const useReduxConnect = () => ({
            // tslint:disable-next-line no-any
            renderRouterContext: (child: React.ReactNode, props: any) => (
              <ReduxAsyncConnect {...props} intl={intl}>
                {child}
              </ReduxAsyncConnect>
            ),
          });

          match(
            {
              routes,
              history: browserHistory,
            },
            (error, redirectLocation, renderProps) => {
              ReactDOM.hydrate(
                <AppContextProvider
                  config={config}
                  intl={intl}
                  store={store}
                  sheetsRegistry={sheetsRegistry}
                >
                  <Router
                    history={browserHistory}
                    routes={routes}
                    {...renderProps}
                    render={applyRouterMiddleware(
                      useScroll((prevRouterProps, { location }) =>
                        Boolean(
                          prevRouterProps &&
                            (location.pathname !==
                              prevRouterProps.location.pathname ||
                              location.query.page !==
                                prevRouterProps.location.query.page),
                        ),
                      ),
                      useReduxConnect(),
                    )}
                  />
                </AppContextProvider>,
                rootEl,
                resolve,
              );
            },
          );
        }
      }),
  )
  .then(() => {
    store.dispatch(
      setupMediaQueryListeners(
        global.matchMedia,
        isFeatureEnabled(EnvFlags.mediaQueryNext, store),
      ),
    );
  })
  .then(() => {
    if ('serviceWorker' in global.navigator && config.webApp.enableSW) {
      initServiceWorker(
        getHaSwUrl(
          config.baseDomain,
          Cookies.getJSON(CookiesConstants.PWA_NEXT_ENABLED),
        ),
        {
          origin: global.location.origin,
          onError: reportError,
        },
      );
    }
  })
  .catch(reportError)
  .then(() => {
    global.appReady = true;
  });

loadNonCriticalData(() => services.segment.init().catch(reportError));
